// src/lib/db.js
//
// Repository layer for Death Card. The ONLY place components should
// fetch or mutate data. Wraps Supabase, localCache, and outbox to
// provide a stale-while-revalidate read API and an optimistic +
// queued write API.
//
// Components call db.getX() and db.doX() — they don't know whether
// the data came from cache or network, or whether the write went
// directly or got queued for later. That's the point.

import { supabase } from './supabaseClient'
import cache from './localCache'
import outbox from './outbox'

// --- READ HELPERS ---
//
// Stale-while-revalidate pattern: return cached data immediately if
// present, fire a background refresh, and components re-render when
// fresh data lands via React state updates from their own subscribers
// or polling. If no cache and we're offline, throw — caller must
// handle the offline-empty case.

async function readWithSWR(table, key, fetchFromServer) {
  const cached = await cache.get(table, key)

  if (cached) {
    if (navigator.onLine) {
      fetchFromServer()
        .then(fresh => {
          if (fresh) cache.put(table, fresh).catch(() => {})
        })
        .catch(err => console.warn(`[db] background refresh failed for ${table}:${key}`, err))
    }
    return cached
  }

  if (!navigator.onLine) return null
  const fresh = await fetchFromServer()
  if (fresh) {
    cache.put(table, fresh).catch(() => {})
  }
  return fresh
}

async function readManyWithSWR(table, filterFn, fetchFromServer) {
  const allRows = await cache.getAll(table)  // unfiltered
  const cached = await cache.getAll(table, filterFn)

  if (cached.length > 0) {
    if (navigator.onLine) {
      fetchFromServer()
        .then(fresh => {
          if (Array.isArray(fresh)) {
            cache.putMany(table, fresh).catch(() => {})
          }
        })
        .catch(err => console.warn(`[db] background refresh failed for ${table}`, err))
    }
    return cached
  }

  if (!navigator.onLine) return []
  const fresh = await fetchFromServer()
  if (Array.isArray(fresh)) {
    cache.putMany(table, fresh).then(() => {
    }).catch(err => {
      console.error(`[db.SWR] ${table} cache write FAILED`, err)
    })
  }
  return fresh ?? []
}

// --- USER PROFILE ---

export async function getUserProfile(userId) {
  return readWithSWR('users', userId, async () => {
    const { data } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .limit(1)
    return data?.[0] ?? null
  })
}

// --- ACTIVE EVENTS ---
//
// "Active events the user is joined to" requires a join through
// event_players. We fetch from server, filter to status='active',
// and cache the resulting events.

export async function getActiveEventsForUser(userId) {
  // Always query server — registration data must be current
  // and cache is shared with getAllEvents which pollutes results
  if (!navigator.onLine) {
    // Offline fallback: check localStorage for last known event
    const storedId = localStorage.getItem('dc_active_event')
    if (storedId) {
      const cached = await cache.get('events', storedId)
      return cached ? [cached] : []
    }
    return []
  }

  const { data } = await supabase
    .from('event_players')
    .select('event:events!event_players_event_id_fkey(*)')
    .eq('user_id', userId)
    .in('reg_status', ['approved', 'active'])
  
  // Cross-env: dev runs the 7-state lifecycle (status='live') while prod
  // is still on the legacy 'active' value. Treat both as the runtime-live
  // status until the prod schema migration lands.
  const events = (data ?? [])
    .map(row => row.event)
    .filter(e => e && (e.status === 'live' || e.status === 'active'))

  return events
}

// "Every event the user is registered for" — no event-status filter, so
// a freshly-joined event in 'registration' / 'prep' / 'locked' surfaces
// the moment the event_players row lands. Used by the Events tab to
// populate "My Events". Distinct from getActiveEventsForUser, which
// gates the bounty board and intentionally keeps to live/active.
//
// Bug 35: do NOT filter on reg_status or event_role here. If the user has
// an event_players row at all, the event belongs in their Events tab —
// including co_gms whose row was upgraded from 'player' via MCS commander
// assignment and any future status values not in the IN-list.
export async function getRegisteredEventsForUser(userId) {
  if (!navigator.onLine) {
    const storedId = localStorage.getItem('dc_active_event')
    if (storedId) {
      const cached = await cache.get('events', storedId)
      return cached ? [cached] : []
    }
    return []
  }

  const { data } = await supabase
    .from('event_players')
    .select('event:events!event_players_event_id_fkey(*, organizations(name))')
    .eq('user_id', userId)

  return (data ?? []).map(row => row.event).filter(Boolean)
}

// --- FACTIONS FOR ACTIVE EVENTS ---

export async function getFactionsForEvent(eventId) {
  return readManyWithSWR(
    'event_factions',
    f => f.event_id === eventId,
    async () => {
      const { data } = await supabase
        .from('event_factions')
        .select('*')
        .eq('event_id', eventId)
      return data ?? []
    }
  )
}

// --- USER'S EVENT_PLAYER ROW ---
//
// One row per (user, event) pair. Tells us the user's faction,
// role, MCS node assignment for the given event.

export async function getEventPlayer(userId, eventId) {
  // Composite-key lookup; we cache by event_player.id but key
  // user-side queries by combining the two. Use getAll + filter.
  const cached = (await cache.getAll('event_players',
    ep => ep.user_id === userId && ep.event_id === eventId
  ))[0]

  if (navigator.onLine) {
    supabase
      .from('event_players')
      .select('*')
      .eq('user_id', userId)
      .eq('event_id', eventId)
      .limit(1)
      .then(({ data }) => {
        if (data?.[0]) cache.put('event_players', data[0]).catch(() => {})
      })
  }

  if (cached) return cached
  if (!navigator.onLine) return null
  const { data } = await supabase
    .from('event_players')
    .select('*')
    .eq('user_id', userId)
    .eq('event_id', eventId)
    .limit(1)
  return data?.[0] ?? null
}

// --- BOUNTIES (ACTIVE STATUS, NON-SELF TARGET) ---
//
// Returns every active bounty in the event whose target isn't the caller.
// Type-aware faction filtering (kill → opposing only, defend → same faction
// only) happens in PlayerView so defend bounties on a same-faction VIP
// remain visible to the protecting faction. The previous version excluded
// all same-faction targets here, which silently dropped defend visibility.
// userFactionId is kept on the signature for back-compat with callers but
// is no longer used for filtering.
// eslint-disable-next-line no-unused-vars
export async function getActiveBountiesForEvent(eventId, userId, userFactionId = null) {
  const filter = b =>
    (eventId === null || b.event_id === eventId) &&
    b.status === 'active' &&
    b.target_id !== userId

  return readManyWithSWR('bounties', filter, async () => {
    let query = supabase
      .from('bounties')
      .select('*, target:users!bounties_target_id_fkey(display_name, callsign), issuer:users!bounties_issued_by_fkey(display_name)')
      .eq('status', 'active')
      .neq('target_id', userId)
      .order('issued_at', { ascending: false })

    if (eventId) query = query.eq('event_id', eventId)

    const { data } = await query
    // Clear stale bounties before caching fresh set so a row that's no
    // longer in the active set (status flipped to claimed/cancelled, etc.)
    // doesn't keep matching the cache filter on the next read.
    await cache.clear('bounties').catch(() => {})
    return data ?? []
  })
}

export async function getMyBounties(userId) {
  // Bounties where I am the target. Cached so player sees incoming
  // bounties on themselves even when offline (situational awareness).
  const filter = b =>
    b.target_id === userId &&
    b.status === 'active'

  return readManyWithSWR('bounties', filter, async () => {
    const { data } = await supabase
      .from('bounties')
      .select('*, issuer:users!bounties_issued_by_fkey(display_name)')
      .eq('target_id', userId)
      .eq('status', 'active')
    return data ?? []
  })
}

// --- PLAYER STATS ---
 
export async function getPlayerStats(userId) {
  return readWithSWR('player_stats', userId, async () => {
    const { data } = await supabase
      .from('player_stats')
      .select('*')
      .eq('user_id', userId)
      .limit(1)
    return data?.[0] ?? null
  })
}
 
// --- PENDING HONORS (kills awaiting confirmation) ---
 
export async function getPendingHonors(userId) {
  // 'pending' = awaiting target's honor decision. 'unconfirmed' is the
  // post-dishonor terminal state in the new model, NOT the await-decision
  // state — so we filter for pending here.
  return readManyWithSWR(
    'kill_log',
    k => k.target_id === userId && k.status === 'pending',
    async () => {
      const { data } = await supabase
        .from('kill_log')
        .select('*, killer:users!kill_log_killer_id_fkey(display_name, callsign)')
        .eq('target_id', userId)
        .eq('status', 'pending')
        .order('scanned_at', { ascending: false })
      return data ?? []
    }
  )
}
 
// --- MY TEAMS ---
 
export async function getMyTeams(userId) {
  return readManyWithSWR(
    'team_members',
    tm => tm.user_id === userId,
    async () => {
      const { data } = await supabase
        .from('team_members')
        .select('team_role, teams(*, team_stats(*))')
        .eq('user_id', userId)
        .order('joined_at', { ascending: false })
      return data ?? []
    }
  )
}
 
// --- MY PURSUITS (bounties I'm actively pursuing) ---
 
export async function getMyPursuits(userId, eventId = null) {
  return readManyWithSWR(
    'bounty_pursuits',
    p => p.user_id === userId && (eventId === null || p.event_id === eventId),
    async () => {
      let query = supabase
        .from('bounty_pursuits')
        .select('*')
        .eq('user_id', userId)
      if (eventId) query = query.eq('event_id', eventId)
      const { data } = await query
      return data ?? []
    }
  )
}
 
// --- USER FACTION FOR EVENT ---
 
export async function getUserFaction(userId, eventId) {
  const key = `${userId}_${eventId}`
  return readWithSWR('event_players', key, async () => {
    const { data } = await supabase
      .from('event_players')
      .select('faction_id')
      .eq('user_id', userId)
      .eq('event_id', eventId)
      .limit(1)
    return data?.[0] ?? null
  })
}
 
// --- ALL EVENTS (Dashboard) ---
 
export async function getAllEvents() {
  return readManyWithSWR(
    'events',
    () => true,
    async () => {
      const { data } = await supabase
        .from('events')
        .select('*, organizations(name)')
        .order('created_at', { ascending: false })
      return data ?? []
    }
  )
}

// Events visible to a user on the Dashboard.
//
// Union of two paths:
//   1. Org-side membership — any event in an org the user is a member of
//      (Bug 32: org_gm scoped to one org shouldn't see other orgs' events).
//   2. Event-side GM-tier role — any event_players row where event_role
//      is gm or co_gm (Bug 61: co_gms added at event level have NO
//      org_members row, so the org-only path returned [] and they had
//      no events to drill into → command center unreachable).
//
// Platform staff fall back to getAllEvents (god-mode).
export async function getMyOrgEvents(userId) {
  if (!userId) return []
  return readManyWithSWR(
    'events',
    () => true,
    async () => {
      const [{ data: oms }, { data: eps }] = await Promise.all([
        supabase.from('org_members').select('org_id').eq('user_id', userId),
        supabase.from('event_players').select('event_id')
          .eq('user_id', userId)
          .in('event_role', ['gm', 'co_gm']),
      ])
      const orgIds = (oms || []).map(o => o.org_id).filter(Boolean)
      const eventIds = (eps || []).map(e => e.event_id).filter(Boolean)
      if (orgIds.length === 0 && eventIds.length === 0) return []
      const filters = []
      if (orgIds.length)   filters.push(`org_id.in.(${orgIds.join(',')})`)
      if (eventIds.length) filters.push(`id.in.(${eventIds.join(',')})`)
      const { data } = await supabase
        .from('events')
        .select('*, organizations(name)')
        .or(filters.join(','))
        .order('created_at', { ascending: false })
      return data ?? []
    }
  )
}

// --- EVENT BY ID ---
 
export async function getEventById(eventId) {
  return readWithSWR('events', eventId, async () => {
    const { data } = await supabase
      .from('events')
      .select('id, name, status')
      .eq('id', eventId)
      .limit(1)
    return data?.[0] ?? null
  })
}
 
// --- IS ORG ADMIN CHECK ---

export async function getIsOrgAdmin(userId) {
  return readWithSWR('org_members', `admin_${userId}`, async () => {
    // ANU-46 — platform staff bypass org_members entirely. Their
    // platform_role gives blanket access to every org without needing
    // to be added to each one. Most callers already short-circuit via
    // isPlatformStaff(profile) before calling this; the bare check
    // here is defense-in-depth for paths that don't.
    const { data: u } = await supabase
      .from('users')
      .select('platform_role, role')
      .eq('id', userId)
      .limit(1)
    if (u?.[0]?.platform_role || u?.[0]?.role === 'm55_admin') return true

    const { data } = await supabase
      .from('org_members')
      .select('id')
      .eq('user_id', userId)
      // ANU-45 — org_gm needs the Org button too. The "admin" name on
      // this helper is historical; it gates the org-management nav
      // entry, not destructive admin actions, so org_gm belongs in the
      // include list alongside org_owner / org_admin. Destructive
      // operations inside OrgAdmin still gate on stricter checks.
      .in('org_role', ['org_owner', 'org_admin', 'org_gm'])
      .limit(1)
    return data?.[0] ? true : false
  })
}

// Hardening Track 2.1 / Bug 44 — event-creation gate.
// Distinct from getIsOrgAdmin: an org_gm should also be able to spin up
// events for their org (per the spec, event creation is allowed for
// org_owner, org_admin, org_gm, AND platform staff). co_gm — which is an
// event-role, not an org-role — should NOT see the Create Op button.
export async function getCanCreateEvent(userId) {
  return readWithSWR('org_members', `can_create_event_${userId}`, async () => {
    // ANU-46 — platform staff bypass org_members membership check.
    const { data: u } = await supabase
      .from('users')
      .select('platform_role, role')
      .eq('id', userId)
      .limit(1)
    if (u?.[0]?.platform_role || u?.[0]?.role === 'm55_admin') return true
    const { data } = await supabase
      .from('org_members')
      .select('id')
      .eq('user_id', userId)
      .in('org_role', ['org_owner', 'org_admin', 'org_gm'])
      .limit(1)
    return data?.[0] ? true : false
  })
}


// --- WRITES (with outbox queueing) ---
//
// Each write follows the same shape:
//   1. Build payload + idempotency key
//   2. Try server immediately if online
//   3. On any failure (network OR exception), queue to outbox
//   4. Return success — caller never blocks on the network

export async function claimKill({ killerId, targetId, eventId, bountyId, scanSource = 'qr_camera', killType = null, pointsAwarded = 0 }) {
  const idempotency_key = crypto.randomUUID()
  const payload = {
    killer_id: killerId,
    target_id: targetId,
    event_id: eventId,
    bounty_id: bountyId,
    scan_source: scanSource,
    scanned_at: new Date().toISOString(),
    idempotency_key,
    kill_type: killType,
    points_awarded: pointsAwarded,
    status: 'pending',
  }

  if (navigator.onLine) {
    try {
      await sendKillClaim(payload, idempotency_key)
      // Invalidate bounties cache so stale entries don't persist
      await cache.clear('bounties').catch(() => {})
      return { success: true, queued: false }
    } catch (err) {
      console.warn('[db] claimKill direct send failed, queueing', err)
    }
  }

  await outbox.enqueue('claimKill', payload)
  return { success: true, queued: true }
}

export async function commitToBounty({ bountyId, userId, factionId }) {
  const payload = { bounty_id: bountyId, user_id: userId, faction_id: factionId }

  if (navigator.onLine) {
    try {
      await sendBountyPursuit(payload)
      return { success: true, queued: false }
    } catch (err) {
      console.warn('[db] commitToBounty direct send failed, queueing', err)
    }
  }

  await outbox.enqueue('commitToBounty', payload)
  return { success: true, queued: true }
}

async function sendBountyPursuit(payload) {
  const { error } = await supabase.from('bounty_pursuits').insert(payload)
  if (error) {
    const wrapped = new Error(error.message)
    wrapped.status = error.code?.startsWith('23') ? 400 : 500
    throw wrapped
  }
  // Bust the SWR cache so the next read returns the new row from the
  // server instead of the pre-insert snapshot in IndexedDB.
  await cache.clear('bounty_pursuits').catch(() => {})
}

export async function standDown({ bountyId, userId }) {
  const payload = { bounty_id: bountyId, user_id: userId }

  if (navigator.onLine) {
    try {
      await sendStandDown(payload)
      return { success: true, queued: false }
    } catch (err) {
      console.warn('[db] standDown direct send failed, queueing', err)
    }
  }

  await outbox.enqueue('standDown', payload)
  return { success: true, queued: true }
}

// --- CONFIRM / DISMISS KILLS ---
 
export async function confirmKill({ killId }) {
  const payload = { kill_id: killId }

  if (navigator.onLine) {
    try {
      await sendConfirmKill(payload)
      await cache.clear('kill_log').catch(() => {})
      await cache.clear('player_stats').catch(() => {})
      return { success: true, queued: false }
    } catch (err) {
      console.warn('[db] confirmKill direct send failed, queueing', err)
    }
  }

  await outbox.enqueue('confirmKill', payload)
  return { success: true, queued: true }
}

export async function dismissKill({ killId }) {
  const payload = { kill_id: killId }

  if (navigator.onLine) {
    try {
      await sendDismissKill(payload)
      await cache.clear('kill_log').catch(() => {})
      await cache.clear('player_stats').catch(() => {})
      return { success: true, queued: false }
    } catch (err) {
      console.warn('[db] dismissKill direct send failed, queueing', err)
    }
  }

  await outbox.enqueue('dismissKill', payload)
  return { success: true, queued: true }
}

async function sendConfirmKill(payload) {
  // RPC atomically: flips kill_log.status pending → confirmed,
  // applies killer stats (kills + bounty + org_confirmed bonus) unless
  // kill_type is team_kill/faction_kill, and increments target deaths.
  const { error } = await supabase.rpc('confirm_kill_with_stats', {
    p_kill_id: payload.kill_id,
  })
  if (error) {
    const wrapped = new Error(error.message)
    wrapped.status = 500
    throw wrapped
  }
}

async function sendDismissKill(payload) {
  // RPC atomically: flips kill_log.status pending → unconfirmed (NOT
  // 'dismissed' — that value is deprecated), applies reduced stats
  // (uses org_unconfirmed bonus, no career_confirmed_kills bump).
  const { error } = await supabase.rpc('dismiss_kill_with_stats', {
    p_kill_id: payload.kill_id,
  })
  if (error) {
    const wrapped = new Error(error.message)
    wrapped.status = 500
    throw wrapped
  }
}

// --- HANDLERS (registered with outbox at startup) ---
//
// These do the actual Supabase work. Outbox calls them when draining.
// They MUST throw on failure — outbox uses the throw to decide retry vs stuck.

async function sendKillClaim(payload) {
  const { error } = await supabase.from('kill_log').insert(payload)
  if (error) {
    const wrapped = new Error(error.message)
    wrapped.status = error.code?.startsWith('23') ? 400 : 500
    throw wrapped
  }

  // Stats are no longer awarded at scan time — they happen when the target
  // honors (confirm_kill_with_stats) or dishonors (dismiss_kill_with_stats).
  // kill_log.status defaults to 'pending'; payload sets it explicitly.

  // --- Bounty fulfillment check ---
  if (payload.bounty_id) {
    const { data: bounty } = await supabase
      .from('bounties')
      .select('id, max_claims, status')
      .eq('id', payload.bounty_id)
      .limit(1)

    const b = bounty?.[0]
    if (b && b.status === 'active' && b.max_claims !== null) {
      const { count } = await supabase
        .from('kill_log')
        .select('id', { count: 'exact', head: true })
        .eq('bounty_id', payload.bounty_id)

      if (count >= b.max_claims) {
        await supabase
          .from('bounties')
          .update({
            status: 'claimed',
            claimed_by: payload.killer_id,
            claimed_at: new Date().toISOString(),
          })
          .eq('id', payload.bounty_id)
      }
    }
  }
}

async function sendStandDown(payload) {
  const { error } = await supabase
    .from('bounty_pursuits')
    .delete()
    .eq('bounty_id', payload.bounty_id)
    .eq('user_id', payload.user_id)
  if (error) {
    const wrapped = new Error(error.message)
    wrapped.status = 500
    throw wrapped
  }
  // Bust the SWR cache so the next read goes to the server. Without this,
  // db.getMyPursuits returns the now-deleted row from IndexedDB and the
  // ghost pursuit reappears in PlayerView's pursuits state.
  await cache.clear('bounty_pursuits').catch(() => {})
}

export async function getEventStats(userId, eventId) {
  if (!eventId || !navigator.onLine) return null

  try {
    // v_player_event_stats is the single source of truth — replaces the
    // hand-rolled fan of kill_log + bounties COUNT queries that used to
    // live here. View columns are (kills_confirmed, kills_unconfirmed,
    // kills_total, deaths, bounties_claimed, bounties_survived,
    // bounties_issued, fragos_completed, intel_uploaded, session_points,
    // total_points). Map to the legacy keys PlayerView already reads so
    // the UI stays unchanged.
    const { data, error } = await supabase
      .from('v_player_event_stats')
      .select('*')
      .eq('user_id', userId)
      .eq('event_id', eventId)
      .limit(1)
    if (error) {
      console.warn('[db] getEventStats view query failed:', error)
      return null
    }
    const row = data?.[0]
    if (!row) {
      // No row in the view = the player isn't on this event's roster (or
      // hasn't done anything yet). Return zeros so the stats bar still
      // renders rather than collapsing to "offline".
      return {
        total_points: 0,
        total_deaths: 0,
        career_confirmed_kills: 0,
        career_unconfirmed_kills: 0,
        bounties_claimed: 0,
        bounties_survived: 0,
        is_event_stats: true,
      }
    }
    return {
      total_points: row.total_points ?? 0,
      total_deaths: row.deaths ?? 0,
      career_confirmed_kills: row.kills_confirmed ?? 0,
      career_unconfirmed_kills: row.kills_unconfirmed ?? 0,
      bounties_claimed: row.bounties_claimed ?? 0,
      bounties_survived: row.bounties_survived ?? 0,
      fragos_completed: row.fragos_completed ?? 0,
      session_points: row.session_points ?? 0,
      is_event_stats: true,
    }
  } catch (err) {
    console.error('[db] getEventStats failed:', err)
    return null
  }
}

// Career stats from v_player_career_stats. Used for the EVENT/CAREER
// toggle in PlayerView. Returns career totals + KD ratio. Mapped onto the
// same key names the stats bar already understands.
export async function getCareerStats(userId) {
  if (!navigator.onLine) return null
  try {
    const { data, error } = await supabase
      .from('v_player_career_stats')
      .select('*')
      .eq('user_id', userId)
      .limit(1)
    if (error) {
      console.warn('[db] getCareerStats view query failed:', error)
      return null
    }
    const row = data?.[0]
    if (!row) {
      return {
        total_points: 0,
        total_deaths: 0,
        career_confirmed_kills: 0,
        career_unconfirmed_kills: 0,
        bounties_claimed: 0,
        bounties_survived: 0,
        events_played: 0,
        kd_ratio: 0,
        is_event_stats: false,
      }
    }
    return {
      total_points: row.career_total_points ?? 0,
      total_deaths: row.career_deaths ?? 0,
      career_confirmed_kills: row.career_kills_confirmed ?? 0,
      career_unconfirmed_kills: row.career_kills_unconfirmed ?? 0,
      bounties_claimed: row.career_bounties_claimed ?? 0,
      bounties_survived: row.career_bounties_survived ?? 0,
      bounties_issued: row.career_bounties_issued ?? 0,
      fragos_completed: row.career_fragos_completed ?? 0,
      intel_uploaded: row.career_intel_uploaded ?? 0,
      events_played: row.events_played ?? 0,
      kd_ratio: Number(row.kd_ratio ?? 0),
      is_event_stats: false,
    }
  } catch (err) {
    console.error('[db] getCareerStats failed:', err)
    return null
  }
}

// Faction-level scoreboard for the GM SITREP tab. v_player_event_stats
// has a row per (user, event) with faction_id + total_points already
// computed; we group client-side so the UI can render a sorted leaderboard
// and keep showing player counts alongside the points totals.
export async function getEventFactionScores(eventId) {
  if (!eventId || !navigator.onLine) return []
  try {
    const { data, error } = await supabase
      .from('v_player_event_stats')
      .select('user_id, faction_id, total_points, kills_confirmed, deaths, bounties_claimed, fragos_completed')
      .eq('event_id', eventId)
    if (error) {
      console.warn('[db] getEventFactionScores view query failed:', error)
      return []
    }
    const groups = new Map()
    ;(data || []).forEach(row => {
      const fid = row.faction_id || null
      if (!groups.has(fid)) groups.set(fid, { faction_id: fid, players: 0, total_points: 0, kills_confirmed: 0, deaths: 0, bounties_claimed: 0, fragos_completed: 0 })
      const g = groups.get(fid)
      g.players += 1
      g.total_points += row.total_points || 0
      g.kills_confirmed += row.kills_confirmed || 0
      g.deaths += row.deaths || 0
      g.bounties_claimed += row.bounties_claimed || 0
      g.fragos_completed += row.fragos_completed || 0
    })
    return [...groups.values()]
  } catch (err) {
    console.error('[db] getEventFactionScores failed:', err)
    return []
  }
}

// Aggregate team stats from v_team_aggregate_stats (one row per team).
export async function getTeamAggregateStats(teamId) {
  if (!teamId || !navigator.onLine) return null
  try {
    const { data, error } = await supabase
      .from('v_team_aggregate_stats')
      .select('*')
      .eq('team_id', teamId)
      .limit(1)
    if (error) {
      console.warn('[db] getTeamAggregateStats view query failed:', error)
      return null
    }
    return data?.[0] || null
  } catch (err) {
    console.error('[db] getTeamAggregateStats failed:', err)
    return null
  }
}

// Per-member breakdown for a team (one row per (user, event) the member
// participated in). Caller can group by user_id for a per-member rollup.
export async function getTeamMemberStats(teamId) {
  if (!teamId || !navigator.onLine) return []
  try {
    const { data, error } = await supabase
      .from('v_team_stats')
      .select('*')
      .eq('team_id', teamId)
    if (error) {
      console.warn('[db] getTeamMemberStats view query failed:', error)
      return []
    }
    return data || []
  } catch (err) {
    console.error('[db] getTeamMemberStats failed:', err)
    return []
  }
}

// --- CHECKPOINT SCAN (offline path) ---
//
// scan_checkpoint() requires online context (state machine reads/writes
// inside one transaction). When offline, queue the scan; the drain
// re-fires it via the same RPC once connectivity returns. We can't
// surface live timer/state in offline mode, so the scanner UI just
// confirms the queue.

export async function queueCheckpointScan({ checkpointId, userId, eventId }) {
  await outbox.enqueue('scanCheckpoint', { checkpointId, userId, eventId, queuedAt: new Date().toISOString() })
}

async function sendCheckpointScan(payload) {
  const { error } = await supabase.rpc('scan_checkpoint', { p_checkpoint_id: payload.checkpointId })
  if (error) {
    const wrapped = new Error(error.message)
    wrapped.status = 500
    throw wrapped
  }
}

// --- INITIALIZATION ---
//
// Called once at app startup (from main.jsx or AuthContext).
// Registers outbox handlers and starts the drain timer.

export function init() {
  outbox.register('claimKill', sendKillClaim)
  outbox.register('commitToBounty', sendBountyPursuit)
  outbox.register('standDown', sendStandDown)
  outbox.register('confirmKill', sendConfirmKill)
  outbox.register('dismissKill', sendDismissKill)
  outbox.register('scanCheckpoint', sendCheckpointScan)
  outbox.start()
}

// --- CACHE LIFECYCLE ---

export async function clearCacheOnSignOut() {
  await cache.clearAll()
  await outbox.clearAll()
}

// Bug 37: mutations performed outside db.js (direct supabase.from() calls
// in components) don't bust the SWR caches the read helpers populate, so
// the next read returns the pre-mutation snapshot and the UI looks stale
// until the user hard-refreshes. Callers invoke this immediately after a
// mutation lands, then refetch — the next read sees empty cache and goes
// straight to the network. Variadic so a single call can sweep several
// related tables (e.g. bounties + event_players after a player add).
export async function bustCache(...tables) {
  await Promise.all(
    tables.map(t => cache.clear(t).catch(() => {}))
  )
}

// --- DEFAULT EXPORT ---

export default {
  // Reads
  getUserProfile,
  getActiveEventsForUser,
  getRegisteredEventsForUser,
  getFactionsForEvent,
  getEventPlayer,
  getActiveBountiesForEvent,
  getMyBounties,
  getPlayerStats,
  getPendingHonors,
  getMyTeams,
  getMyPursuits,
  getUserFaction,
  getAllEvents,
  getMyOrgEvents,
  getEventById,
  getIsOrgAdmin,
  getCanCreateEvent,
  getEventStats,
  getCareerStats,
  getEventFactionScores,
  getTeamAggregateStats,
  getTeamMemberStats,
  // Writes
  queueCheckpointScan,
  claimKill,
  commitToBounty,
  standDown,
  confirmKill,
  dismissKill,
  // Lifecycle
  init,
  clearCacheOnSignOut,
  bustCache,
}